// Fetch helpers for the /me/substance/* endpoints — see spec §5 / §6.
//
// Uses fetch + credentials:"include" (matching formula_api / ingredient-search_api).
// axios won't work here because in dev the client (localhost:5174) and server
// (localhost:3001) are cross-origin and the Clerk session cookie has to ride
// along for `requireAuth()` to accept the request. credentials:"include" tells
// the browser to send the cookie; axios's default omits credentials and the
// request gets bounced with a 302 to the sign-in page.

import { serverURL } from "@/utils/serverURL";
import type {
  FileUrlResponse,
  InventoryFileSlot,
  InventoryResponse,
  MarketProductsResponse,
  SafetyPayload,
  SubstanceKind,
  SubstanceSupplyFlags,
} from "@/utils/types/substance";

async function jsonOrThrow<T>(res: Response, endpoint: string): Promise<T> {
  if (!res.ok) {
    let detail = "";
    try { detail = await res.text(); } catch { /* ignore */ }
    throw new Error(`${endpoint} failed: ${res.status} ${detail.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

export async function fetchSubstanceInventory(
  kind: SubstanceKind,
  id: number,
  signal?: AbortSignal,
): Promise<InventoryResponse> {
  const res = await fetch(
    serverURL`/me/substance/${kind}/${id}/inventory`,
    { credentials: "include", signal },
  );
  return jsonOrThrow(res, `GET /me/substance/${kind}/${id}/inventory`);
}

export async function fetchSubstanceMarketProducts(
  kind: SubstanceKind,
  id: number,
  signal?: AbortSignal,
): Promise<MarketProductsResponse> {
  const res = await fetch(
    serverURL`/me/substance/${kind}/${id}/market-products`,
    { credentials: "include", signal },
  );
  return jsonOrThrow(res, `GET /me/substance/${kind}/${id}/market-products`);
}

export async function fetchSubstanceSafety(
  kind: SubstanceKind,
  id: number,
  signal?: AbortSignal,
): Promise<SafetyPayload> {
  const res = await fetch(
    serverURL`/me/substance/${kind}/${id}/safety`,
    { credentials: "include", signal },
  );
  return jsonOrThrow(res, `GET /me/substance/${kind}/${id}/safety`);
}

export async function fetchSubstanceSupplyFlagsBatch(
  keys: string[],
  signal?: AbortSignal,
): Promise<{ summaries: Record<string, SubstanceSupplyFlags> }> {
  const res = await fetch(
    serverURL`/me/substance/batch-supply-flags`,
    {
      method:      "POST",
      credentials: "include",
      headers:     { "Content-Type": "application/json" },
      body:        JSON.stringify({ keys }),
      signal,
    },
  );
  return jsonOrThrow(res, "POST /me/substance/batch-supply-flags");
}

// Coalesces all per-substance supply-flags calls made within the same
// browser tick into a SINGLE batched POST. Each useQuery in
// `useSubstanceSupplyFlags` keeps its own cache slot (so adding one
// ingredient only fetches that one row's flags), but the network layer
// still batches.
//
// Lifecycle:
//   1. First call schedules a 0 ms setTimeout (NOT queueMicrotask — we
//      want every queryFn that React Query kicks off in the current tick
//      to have pushed before we flush, and RQ's internal queryFn dispatch
//      can land in its own microtask, which queueMicrotask wouldn't
//      necessarily out-order).
//   2. Subsequent calls push onto the same queue (the duplicate-key case
//      is rare but the server's response is keyed so duplicates are
//      harmless; we dedupe anyway to keep the wire body tight).
//   3. When the timer fires, ONE HTTP POST goes out with the merged key
//      list. Each per-call promise resolves with its substance's flags
//      (or null when the server's `summaries` object lacks that key).
type PendingEntry = {
  key:     string;
  resolve: (flags: SubstanceSupplyFlags | null) => void;
  reject:  (err: unknown) => void;
};
let pendingBatch: PendingEntry[] | null = null;

export function fetchSubstanceSupplyFlagsOne(
  key: string,
): Promise<SubstanceSupplyFlags | null> {
  return new Promise<SubstanceSupplyFlags | null>((resolve, reject) => {
    if (pendingBatch === null) {
      pendingBatch = [];
      const fire = async () => {
        const batch = pendingBatch!;
        pendingBatch = null;
        const keys = Array.from(new Set(batch.map((e) => e.key)));
        try {
          const res = await fetchSubstanceSupplyFlagsBatch(keys);
          for (const entry of batch) {
            entry.resolve(res.summaries[entry.key] ?? null);
          }
        } catch (err) {
          for (const entry of batch) entry.reject(err);
        }
      };
      setTimeout(() => { void fire(); }, 0);
    }
    pendingBatch.push({ key, resolve, reject });
  });
}

export async function fetchSubstanceFileUrl(
  kind: SubstanceKind,
  id: number,
  inventoryId: string,
  slot: InventoryFileSlot,
): Promise<FileUrlResponse> {
  const res = await fetch(
    serverURL`/me/substance/${kind}/${id}/inventory/${inventoryId}/file/${slot}`,
    { credentials: "include" },
  );
  return jsonOrThrow(res, `GET /me/substance/${kind}/${id}/inventory/${inventoryId}/file/${slot}`);
}
